import { clientApi, logout, refreshClient } from "@/utils/api";
import { setToken } from "@/utils/token";

import { TLoginReqDto, TLoginResDto } from "@/api/services/main/auth/dtos/login.dto";
import Cookies from "js-cookie";
import { TRequestResetPasswordReqDto, TRequestResetPasswordResDto } from "@/api/services/main/auth/dtos/request-reset-password.dto";
import { TGetMeResDto } from "@/api/services/main/auth/dtos/get-me.dto";
import { useQuery } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { useCallback } from "react";
import { TUserPermission, USER_TYPE } from "@/types/enum";
import { TRefreshTokenReqDto, TRefreshTokenResDto } from "@/api/services/main/auth/dtos/refresh-token.dto";
import { TResetPasswordReqDto, TResetPasswordResDto } from "@/api/services/main/auth/dtos/reset-password.dto";
import { TSendEmailToChangeUserEmailReqDto, TSendEmailToChangeUserEmailResDto } from "@/api/services/main/auth/dtos/send-email-to-change-email.dto";
import { TUpdateUserEmailReqDto, TUpdateUserEmailResDto } from "@/api/services/main/auth/dtos/update-user-email.dto";
import { TSetPasswordReqDto, TSetPasswordResDto } from "@/api/services/main/auth/dtos/set-password.dto";
import { TLogoutResDto } from "@/api/services/main/auth/dtos/logout.dto";
import { TValidateNewEmailReqDto, TValidateNewEmailResDto } from "@/api/services/main/auth/dtos/validate-new-email-exists.dto";
import { TValidateTokenReqDto, TValidateTokenResDto, TValidateTokenRes } from "@/api/services/main/auth/dtos/validate-token.dto";

export const loginApi = async (payload: TLoginReqDto): Promise<TLoginResDto> => {
  const rs = await clientApi.post<TLoginResDto>("/login", payload);

  const { data } = rs.data;
  logout();
  setToken(data.accessToken, data.refreshToken);
  Cookies.set("email", data.email);
  Cookies.set("ownerId", data.ownerId.toString());
  Cookies.set("userId", data.userId.toString());
  Cookies.set("isUser", data.isUser.toString());
  return rs.data;
};

export const requestResetPasswordApi = async (payload: TRequestResetPasswordReqDto): Promise<TRequestResetPasswordResDto> => {
  const { data } = await clientApi.post<TRequestResetPasswordResDto>("/request-reset-password", payload);
  return data;
};

export const fetchMeApi = async (): Promise<TGetMeResDto> => {
  const { data } = await clientApi.get<TGetMeResDto>("/get-me");
  return data;
};

export const useMe = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.USER.FETCH_ME],
    queryFn: fetchMeApi,
  });
};

export const usePermissions = () => {
  const { data, isLoading } = useMe();
  const user = data?.data;
  const permissions = user?.permissions;
  const hasPermission = useCallback(
    (code: TUserPermission) => {
      if (isLoading || !user) return false;
      if (user.type !== USER_TYPE.SUPPORT_USER) return true;
      if (!user.permissions) return false;
      return user.permissions[code] ?? false;
    },
    [isLoading, user?.type, user?.permissions],
  );
  return { permissions, hasPermission, isLoading };
};

// Shared refresh promise to prevent concurrent refresh calls (race condition between interceptor, withAuth, SocketProvider)
let refreshPromise: Promise<TRefreshTokenResDto> | null = null;

// Use refreshClient (no auth header, no interceptors) to avoid sending expired token
export const refreshTokenApi = async (payload: TRefreshTokenReqDto): Promise<TRefreshTokenResDto> => {
  // Reuse in-flight refresh request to avoid race conditions
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const rs = await refreshClient.post<TRefreshTokenResDto>("/refresh-token", payload);
      const { data } = rs.data;
      // Cognito REFRESH_TOKEN_AUTH may not return a new refreshToken — keep the existing one
      setToken(data.accessToken, data.refreshToken || payload.refreshToken);
      return rs.data;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

export const setPasswordApi = async (payload: TSetPasswordReqDto): Promise<TSetPasswordResDto> => {
  const { data } = await clientApi.post<TSetPasswordResDto>("/set-password", payload);
  return data;
};

export const resetPasswordApi = async (payload: TResetPasswordReqDto): Promise<TResetPasswordResDto> => {
  const { data } = await clientApi.post<TResetPasswordResDto>("/reset-password", payload);
  return data;
};

export const sendEmailToChangeEmailApi = async (payload: TSendEmailToChangeUserEmailReqDto): Promise<TSendEmailToChangeUserEmailResDto> => {
  const { data } = await clientApi.post<TSendEmailToChangeUserEmailResDto>("/send-email-to-change-user-email", payload);
  return data;
};

export const updateUserEmailApi = async (payload: TUpdateUserEmailReqDto): Promise<TUpdateUserEmailResDto> => {
  const { data } = await clientApi.post<TUpdateUserEmailResDto>("/update-user-email", payload);
  return data;
};

export const logoutApi = async (): Promise<TLogoutResDto> => {
  const rs = await clientApi.post<TLogoutResDto>("/logout");
  return rs?.data;
};

export const validateNewEmailExistsApi = async (payload: TValidateNewEmailReqDto): Promise<TValidateNewEmailResDto> => {
  const { data } = await clientApi.post<TValidateNewEmailResDto>("/validate-new-email-exists", payload);
  return data;
};

export const validateTokenApi = async (payload: TValidateTokenReqDto): Promise<TValidateTokenRes> => {
  const response = await clientApi.post<TValidateTokenResDto>("/validate-token", payload);
  return response.data.data;
};
